/**
 * Opening a figure to what composes it (REQ-021 AC-010, AC-009).
 *
 * The panel is an expansion under the tile, beside the definition, and not a
 * dialog. The reason is the reconciliation: the rows are shown against the
 * figure they are supposed to add up to, and a dialog would cover the figure at
 * the moment a reader wanted to compare the two. It also keeps the answer where
 * the question was asked — the trace of MRR means nothing away from MRR — and
 * it mounts only when opened, so ten tiles cost ten requests only if somebody
 * opens ten of them.
 *
 * What the six shapes must never be allowed to blur into each other:
 *
 * - **`refused`** is a decision, not an absence and not a failure. These records
 *   are health-adjacent and are shown to nobody, so the panel states the policy
 *   and offers nothing to retry — a retry affordance would say "try again with
 *   more luck", which is exactly the reading AC-009 forbids.
 * - **`withheld`** is its opposite and is drawn as its opposite: the records
 *   exist, somebody sees them, and this reader is not that somebody. Confusing
 *   the two teaches a reader that "you may not see this" and "nobody may see
 *   this" are the same sentence, and one of them is a permission they can ask
 *   for.
 * - **`aggregate`** says it is a summary and why it stops there, because a
 *   reader who takes three counts for the whole composition has been told
 *   something false by the layout rather than by the payload.
 * - **`unavailable`** is the reason from the payload and never an empty table.
 * - A failed read, a 400 and a 404 are defects with owners and say so.
 * - A shape this screen does not recognise says exactly that. Falling through
 *   every branch draws a header over an empty panel, which reads as the figure
 *   being composed of nothing — the one sentence none of the six shapes says.
 *
 * Nothing here has a place to put a document type, a title, a destination or
 * any health attribute. The backend refuses to send them, and the table draws
 * an allow-list of fields rather than whatever arrives, so a field this screen
 * never agreed to show is named instead of rendered.
 */
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Loader2,
  Lock,
  Minus,
} from "lucide-react";
import { useState } from "react";
import apiClient from "@/services/api/apiClient";
import { formatValue, type MetricUnit } from "./metrics";
import {
  entryColumns,
  fieldLabel,
  isKnownDisclosure,
  MALFORMED_TRACE,
  reconcile,
  reconciliationUnit,
  traceFailure,
  unrecognisedTrace,
  type TraceDisclosure,
  type TraceMeta,
  type TracePayload,
} from "./trace";

const panel = "mt-3 rounded-md border p-3 text-xs";
const heading = "text-xs font-semibold";

function Records({
  disclosure,
  payload,
}: {
  disclosure: Extract<TraceDisclosure, { status: "records" }>;
  payload: TracePayload;
}) {
  const check = reconcile(disclosure, payload.value);
  const unit: MetricUnit = reconciliationUnit(check, payload.metric.unit);
  const { columns, unknown } = entryColumns(disclosure.entries);

  return (
    <div data-testid="trace-records">
      {/* The reconciliation stands above the table, not under it. A reader who
          scrolls a table of fifty rows and meets the disagreement at the bottom
          has already read the rows as the composition. */}
      {check.status === "uncheckable" ? (
        <p
          data-testid="trace-reconciliation-uncheckable"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{check.reason}</span>
        </p>
      ) : check.status === "disagrees" ? (
        <div
          data-testid="trace-reconciliation-disagrees"
          className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-semibold">
              These records do not add up to the figure.
            </span>{" "}
            {check.kind === "sum"
              ? `Their ${fieldLabel(check.field ?? "").toLowerCase()} comes to `
              : "They number "}
            {formatValue(check.rows, unit)}, and the figure they are supposed to
            compose is {formatValue(check.figure, unit)}. One of the two is
            wrong, and the table below explains nothing until it is known which.
          </p>
        </div>
      ) : (
        <p
          data-testid="trace-reconciliation-holds"
          className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-gray-700"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <span>
            {check.kind === "sum"
              ? `The records' ${fieldLabel(check.field ?? "").toLowerCase()} comes to `
              : "The records number "}
            {formatValue(check.rows, unit)}, which is the figure above. Checked
            on this screen against the rows served, not taken from the response.
          </span>
        </p>
      )}

      <p className="mt-2 text-gray-600">{disclosure.reconciliation}</p>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead className="text-gray-500">
            <tr>
              <th className="py-1 pr-3 font-medium">Record</th>
              <th className="py-1 pr-3 font-medium">Kind</th>
              {columns.map((column) => (
                <th key={column} className="py-1 pr-3 font-medium">
                  {fieldLabel(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-gray-700">
            {disclosure.entries.map((entry) => (
              <tr key={entry.ref} data-testid={`trace-entry-${entry.ref}`}>
                <td className="py-1 pr-3 font-medium text-gray-900">
                  {entry.ref}
                </td>
                <td className="py-1 pr-3">{entry.kind}</td>
                {columns.map((column) => (
                  <td key={column} className="py-1 pr-3 tabular-nums">
                    {entry.fields[column] === null ||
                    entry.fields[column] === undefined
                      ? "not set"
                      : String(entry.fields[column])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {unknown.length > 0 && (
        /* Named rather than drawn, and named rather than dropped: a field this
           screen has no heading for is a change nobody told it about, and the
           table is not the place to find out what it contains. */
        <p
          data-testid="trace-unknown-fields"
          className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            These records carry fields this screen does not display:{" "}
            {unknown.join(", ")}. Their values are not shown here, because a
            column is a decision that a field belongs on this dashboard.
          </span>
        </p>
      )}

      {disclosure.truncated && (
        <p data-testid="trace-truncated" className="mt-2 text-gray-600">
          Showing {disclosure.entries.length} of {disclosure.total} records. A
          trace is an explanation rather than an export, so the rest are counted
          and not listed.
        </p>
      )}
    </div>
  );
}

function Aggregate({
  disclosure,
}: {
  disclosure: Extract<TraceDisclosure, { status: "aggregate" }>;
}) {
  return (
    <div data-testid="trace-aggregate">
      <p className="text-gray-700">
        <span className="font-semibold">
          A summary of the counts this figure was built from — not the records
          under them.
        </span>{" "}
        The records are not listed here, and this is why: {disclosure.reason}
      </p>
      <dl className="mt-2 space-y-2">
        {disclosure.parts.map((part) => (
          <div
            key={part.label}
            data-testid={`trace-part-${part.label}`}
            className="border-t border-gray-100 pt-2"
          >
            <dt className="font-medium text-gray-700">{part.label}</dt>
            <dd className="text-gray-600">
              {part.value === null ? (
                <>
                  {/* Never a zero and never a blank: a component that could not
                      be counted is the thing a composite figure is opened to
                      find. */}
                  <span className="flex items-center gap-1.5 font-semibold text-amber-800">
                    <Minus className="h-3 w-3" aria-hidden="true" /> Not counted
                  </span>
                  <span className="mt-0.5 block text-amber-900">
                    {part.reason ??
                      "The payload gave no reason, which is itself a defect."}
                  </span>
                </>
              ) : (
                <span className="font-semibold tabular-nums text-gray-900">
                  {part.value}
                </span>
              )}
              <span className="mt-0.5 block text-gray-500">{part.source}</span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Derived({
  disclosure,
  onOpen,
}: {
  disclosure: Extract<TraceDisclosure, { status: "derived" }>;
  onOpen: (metricId: string) => void;
}) {
  return (
    <div data-testid="trace-derived">
      <p className="text-gray-700">
        <span className="font-semibold">
          This figure is arithmetic over other figures.
        </span>{" "}
        {disclosure.reason}
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {disclosure.from.map((metricId) => (
          <li key={metricId}>
            {/* The names are metric ids, so they open here rather than sending a
                reader to hunt for the tile — the composition of `mrr` is what
                the composition of `arr` was a question about. */}
            <button
              type="button"
              data-testid={`trace-open-${metricId}`}
              onClick={() => onOpen(metricId)}
              className="rounded border border-sky-200 bg-sky-50 px-2 py-1 font-medium text-sky-800 hover:bg-sky-100"
            >
              Open {metricId}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Refused({
  disclosure,
}: {
  disclosure: Extract<TraceDisclosure, { status: "refused" }>;
}) {
  return (
    <div
      data-testid="trace-refused"
      className="flex items-start gap-2 text-gray-800"
    >
      <Ban className="mt-0.5 h-4 w-4 shrink-0 text-gray-600" />
      <div>
        <p className={heading}>
          These records exist and are deliberately not shown.
        </p>
        <p className="mt-1 text-gray-700">{disclosure.reason}</p>
        <p className="mt-1 text-gray-600">
          This is a standing decision under {disclosure.policy}, the same for
          every reader of this screen. It is not a role you could be granted,
          not a store that failed, and not something to ask for again later. The
          count above is the whole of what this dashboard reports about it.
        </p>
      </div>
    </div>
  );
}

function Withheld({
  disclosure,
}: {
  disclosure: Extract<TraceDisclosure, { status: "withheld" }>;
}) {
  return (
    <div
      data-testid="trace-withheld"
      className="flex items-start gap-2 text-amber-900"
    >
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
      <div>
        <p className={heading}>
          Your roles do not reach these records. Somebody else&rsquo;s do.
        </p>
        <p className="mt-1">{disclosure.reason}</p>
        <p className="mt-1" data-testid="trace-required-roles">
          The records are open to {disclosure.requiredRoles.join(" or ")}. Ask
          the holder of that role, or to be granted it — this is a permission,
          not a refusal to show the records to anybody.
        </p>
      </div>
    </div>
  );
}

export default function MetricTrace({ metricId }: { metricId: string }) {
  /**
   * The figures opened so far, deepest last. A derived figure sends a reader to
   * the one it is arithmetic over, and the way back has to be the way they came
   * — the tile behind the panel is still the figure they started from.
   */
  const [path, setPath] = useState<string[]>([metricId]);
  const openId = path[path.length - 1] ?? metricId;
  const previous = path.length > 1 ? path[path.length - 2] : undefined;

  const query = useQuery({
    queryKey: ["dashboard", "trace", openId],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: TracePayload;
        meta: TraceMeta;
      }>(`/staff/dashboard/trace?metric=${encodeURIComponent(openId)}`);
      return response.data;
    },
    retry: false,
    // A composition is opened deliberately and read in a minute. Nothing here
    // polls: a panel that refetched under a reader would move the rows they
    // were adding up.
    staleTime: 5 * 60_000,
  });

  // Read through the error flag rather than around it, as the cohort screen
  // does: react-query keeps the last successful body on a failed refetch, and a
  // table drawn from it would show an earlier composition under a banner saying
  // the composition could not be read.
  const payload = query.isError ? undefined : query.data?.data;
  const disclosure = payload?.disclosure;

  return (
    <section
      data-testid={`trace-${metricId}`}
      className={`${panel} border-gray-200 bg-white`}
    >
      {previous && (
        <button
          type="button"
          data-testid="trace-back"
          onClick={() => setPath((stack) => stack.slice(0, -1))}
          className="mb-2 inline-flex items-center gap-1 font-medium text-sky-700 hover:text-sky-900"
        >
          <ArrowLeft className="h-3 w-3" /> Back to {previous}
        </button>
      )}

      {payload && (
        <p className="mb-2 text-gray-500">
          What composes {payload.metric.label}
          {payload.value.status === "ok"
            ? `, ${formatValue(payload.value.value, payload.metric.unit)} as this composition was read`
            : ", which was not measured on this read"}
          . Owned by {payload.metric.owner}.
        </p>
      )}

      {query.isLoading && (
        <p className="flex items-center gap-1.5 text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Reading what composes
          this figure…
        </p>
      )}

      {query.isError && (
        <p data-testid="trace-error" className="text-red-700">
          {traceFailure(query.error)}
        </p>
      )}

      {!query.isLoading && !query.isError && !disclosure && (
        <p data-testid="trace-malformed" className="text-red-700">
          {MALFORMED_TRACE}
        </p>
      )}

      {disclosure && !isKnownDisclosure(disclosure) && (
        <p data-testid="trace-unrecognised" className="text-red-700">
          {unrecognisedTrace(String(disclosure.status))}
        </p>
      )}

      {payload && disclosure?.status === "records" && (
        <Records disclosure={disclosure} payload={payload} />
      )}
      {disclosure?.status === "aggregate" && (
        <Aggregate disclosure={disclosure} />
      )}
      {disclosure?.status === "derived" && (
        <Derived
          disclosure={disclosure}
          onOpen={(next) => setPath((stack) => [...stack, next])}
        />
      )}
      {disclosure?.status === "refused" && <Refused disclosure={disclosure} />}
      {disclosure?.status === "withheld" && (
        <Withheld disclosure={disclosure} />
      )}
      {disclosure?.status === "unavailable" && (
        <div
          data-testid="trace-unavailable"
          className="flex items-start gap-2 text-amber-900"
        >
          <Minus className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className={heading}>
              There is nothing behind this figure to open.
            </p>
            {/* The payload's own reason, never a paraphrase: an unreachable
                store and a figure nothing computes are different facts, and the
                backend is at pains to tell them apart. */}
            <p className="mt-1">{disclosure.reason}</p>
          </div>
        </div>
      )}
    </section>
  );
}
